import { getFindingInvestigation, saveFindingInvestigation } from "./alertTriageClient.js";
import { runQueryRows } from "./analyticsClient.js";

export const OUTCOMES = [["true_positive", "True positive"], ["false_positive", "False positive"], ["mixed", "Mixed"], ["inconclusive", "Inconclusive"]];
export const investigationError = (e) => e?.grpcCode === "ABORTED" || e?.status === 409
    ? "Another investigator changed this case. Reload and review their changes before saving again."
    : e?.serverMessage || e?.message || "The investigation could not be saved. Retry or reload.";

// A retry retains the exact operation. Never silently retry a decision against
// a newer version, and never share a writer across case identities.
export function investigationWriter(id, initial, { read = getFindingInvestigation, save = saveFindingInvestigation, onSave = () => {} } = {}) {
    let current = initial;
    let pending = null;
    let locked = false;
    return {
        get current() { return current; },
        async refresh() {
            if (locked) throw new Error("Wait for the current save to finish.");
            const next = await read(id);
            current = next;
            pending = null;
            return next;
        },
        async save(change) {
            if (locked) throw new Error("Wait for the current save to finish.");
            const fingerprint = JSON.stringify(change);
            if (pending && pending.fingerprint !== fingerprint) throw new Error("Retry the pending save or reload before making another change.");
            pending ??= { fingerprint, request: { ...change, expectedVersion: current?.version ?? "0", operationId: crypto.randomUUID() } };
            locked = true;
            try {
                const result = await save(id, pending.request);
                current = result;
                pending = null;
                onSave(result);
                globalThis.dispatchEvent?.(new Event("origin-investigation-saved"));
                return result;
            } finally { locked = false; }
        },
    };
}

export async function readInvestigationMembers(id, { signal, read = getFindingInvestigation } = {}) {
    const first = await read(id, { signal });
    const members = [...first.findings];
    let token = first.nextMemberPageToken;
    const seen = new Set();
    while (token && members.length <= 5000) {
        if (seen.has(token)) throw new Error("The evidence cursor repeated. Reload the investigation.");
        seen.add(token);
        const next = await read(id, { signal, memberPageToken: token });
        if (next.version !== first.version) throw new Error("The investigation changed while reading its evidence. Reload and try again.");
        members.push(...next.findings);
        token = next.nextMemberPageToken;
    }
    if (token || members.length > 5000) throw new Error("The investigation exceeds the evidence limit.");
    return { ...first, findings: members };
}

export function findingSelectionSpec(ids, start, end) {
    const field = (suffix) => ({ stepAlias: "f", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_${suffix}` });
    if (!ids.length || ids.length > 50) throw new Error("Select between 1 and 50 findings.");
    if (!(Date.parse(start) < Date.parse(end)) || Date.parse(end) - Date.parse(start) > 90 * 86400000) throw new Error("Choose an evidence window of at most 90 days.");
    return {
        root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING" },
        timeRange: { absolute: { start, end } },
        filter: { pred: { field: field("ID"), op: "OPERATOR_IN", value: ids } },
        output: { dimensions: [["ID", "finding_id"], ["TRACE_ID", "trace_id"], ["TITLE", "title"], ["COMPLETED_AT", "completed_at"]].map(([suffix, outputAlias]) => ({ field: field(suffix), outputAlias })) },
        limit: ids.length,
    };
}

export async function resolveSelectedFindings(ids, start, end, query = runQueryRows) {
    const rows = await query(findingSelectionSpec(ids, start, end));
    const found = new Set(rows.map((r) => r.finding_id));
    if (found.size !== ids.length || ids.some((id) => !found.has(id))) throw new Error("Some selected findings are unavailable in this window. Adjust the dates or selection before saving.");
    return rows;
}

export async function exportInvestigation(id, { includeCitedEvidence = false } = {}) {
    const record = await readInvestigationMembers(id);
    const history = [...record.activity];
    let token = record.nextActivityPageToken;
    // Explicit history budget, with omissions recorded in the package.
    for (let n = 1; token && n < 20; n++) {
        const page = await getFindingInvestigation(id, { activityPageToken: token });
        if (page.version !== record.version) throw new Error("The investigation changed during export. Try again.");
        history.push(...page.activity);
        token = page.nextActivityPageToken;
    }
    let evidence = null;
    if (includeCitedEvidence) {
        const chosen = record.findings.filter((f) => !f.set_aside_at).slice(0, 50);
        if (chosen.length) {
            const times = chosen.map((f) => Date.parse(f.completed_at));
            const start = new Date(Math.min(...times) - 1000).toISOString();
            const end = new Date(Math.max(...times) + 1000).toISOString();
            const findings = await resolveSelectedFindings(chosen.map((f) => f.finding_id), start, end);
            const { readTriageCases } = await import("../pages/agentPolicy/chat/triageCases.js");
            evidence = await readTriageCases({ includeEvidence: true, selectedCase: true, reviewWindow: { fromMs: Date.parse(start), toMs: Date.parse(end) },
                readFindings: async () => findings.map((f) => ({ id: f.finding_id, title: f.title, session: f.trace_id, at: Date.parse(f.completed_at) })) });
            if ((await getFindingInvestigation(id)).version !== record.version) throw new Error("The investigation changed during export. Try again.");
        }
    }
    const packet = {
        schemaVersion: 1, generatedAt: new Date().toISOString(),
        investigation: { ...record, findings: undefined, activity: undefined, workspaceRecord: undefined },
        findings: record.findings, activity: history, citedEvidence: evidence,
        manifest: { findingCount: record.findings.length, activityCount: history.length, historyTruncated: !!token, citedEvidenceRequested: includeCitedEvidence, citedEvidenceFindingLimit: 50, citedEvidencePartLimit: 256,
            included: ["Investigation metadata", "Finding references", "Decision and activity history", ...(evidence ? ["Selected finding titles and cited tool excerpts"] : [])],
            omitted: ["Full Prompt Session content", "Private chat transcripts", "Policy document bodies", "Uncited and non-tool session content", ...(record.findings.length > (evidence?.findings.length || 0) ? ["Evidence beyond selected finding and part caps"] : [])] },
    };
    const json = JSON.stringify(packet, null, 2);
    if (new TextEncoder().encode(json).length > 8_000_000) throw new Error("The export exceeds 8 MB. Narrow the investigation before exporting.");
    return { record, packet, json };
}
